import React, { useEffect, useState } from 'react';
import { Snackbar, Button, Alert, Typography, Box } from '@mui/material';
import SystemUpdateIcon from '@mui/icons-material/SystemUpdate';

const UpdateNotification: React.FC = () => {
  const [showReload, setShowReload] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);

  useEffect(() => {
    // Function to handle when a new service worker is waiting
    const onServiceWorkerUpdate = (registration: ServiceWorkerRegistration) => {
      // Only show update notification if there's a waiting service worker
      if (registration.waiting) {
        setWaitingWorker(registration.waiting);
        setShowReload(true);
      }
    };

    // Check if service workers are supported
    if ('serviceWorker' in navigator) {
      // Get the registration when the service worker is ready
      navigator.serviceWorker.ready.then((registration) => {
        // Check if there's a waiting worker already
        if (registration.waiting) {
          setWaitingWorker(registration.waiting);
          setShowReload(true);
          return;
        }

        // Listen for new service workers being found
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          
          if (!newWorker) return;
          
          // Listen for state changes in the new worker
          newWorker.addEventListener('statechange', () => {
            // If the new worker is installed and we have an active controller,
            // it means we have an update ready
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('New version available!');
              onServiceWorkerUpdate(registration);
            }
          });
        });
      });

      // Check for updates every 60 minutes
      const interval = setInterval(() => {
        console.log('Checking for updates...');
        navigator.serviceWorker.ready.then(registration => {
          registration.update().catch(err => {
            console.error('Error checking for updates:', err);
          });
        });
      }, 60 * 60 * 1000);

      return () => clearInterval(interval);
    }
  }, []);

  // Function to apply the update
  const updateServiceWorker = () => {
    if (!waitingWorker) {
      return;
    }

    // Send message to the waiting service worker to skip waiting
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    
    setShowReload(false);
    
    // Reload the page to apply updates
    window.location.reload();
  };

  return (
    <Snackbar 
      open={showReload}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert 
        severity="info"
        icon={<SystemUpdateIcon />}
        sx={{ width: '100%' }}
        action={
          <Button 
            color="inherit" 
            size="small" 
            onClick={updateServiceWorker}
            sx={{ fontWeight: 'bold' }}
          >
            Update Now
          </Button>
        }
      >
        <Typography variant="body2">
          A new version is available! Update for the latest features.
        </Typography>
      </Alert>
    </Snackbar>
  );
};

export default UpdateNotification; 